import classNames from "classnames";
import * as React from "react";
import { Draggable, DraggableChildrenFn, Droppable } from "react-beautiful-dnd";
import { IconButton, Input, useAlert } from "uxp/components";
import { containsExpressions, generateUUID, hasProperty, hasValue, parseValue } from "../../util";
import { IContextProvider } from "../../uxp";
import {
    addToPath,
    extractPath,
    findCardTemplate,
    getEditorFields,
    getFieldByIdOrKey,
    getMobileCardTemplates,
    getUIFields,
    IBaseProperty,
    IPropertyEditorProps,
    ISerializedUIField,
    IUIFieldDefinition,
    IWDDesignModeProps,
    IWidgetTemplateProps,
    PropertyPath
} from "./common";
import { SelectableItem } from "./SelectableItem";
import { Property, PropertyContainer, PropertyGroup, PropertyGroupTitle, PropertyTitle } from "./ui-props/common";
import { useTag } from "./useTag";
import Readonly from "./Readonly";
import { generateColumns } from "./ui-fields/horizontal-container/HorizontalContainer";
import { ICardWidget } from "../designer/WidgetPanel";
import { IDropdownFieldProperty } from "./ui-fields/dropdown/Dropdown";
import { executeAndBind } from "./data-source-picker/datasource";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { VisibilityProperty } from "./ui-props/VisibilityProperty";
import { IQueryConditionGroup, evaluateConditions } from "./query-builder/QueryBuilder";
import { Conditional } from "./ConditionalComponent";
import { formatFieldPath, IContextData } from "../../hooks/useContextData";
import { MarginProperty } from "./ui-props/MarginProperty";


const cleanAttributeName = (string: string): string => {
    const regExp = /[^a-zA-Z0-9]/gi
    return string.replace(regExp, '')
}

export interface IUseDropArgs {
    /**
     * designer props
     */
    designer?: IWDDesignModeProps,
    /**
     * ui props from the widget
     */
    uiProps: any,
    /**
     * path for the dropable fields (in uiProps)
     */
    propPath?: PropertyPath,
    /**
     * callback on props change
     */
    onChangeFields?: (newFields: ISerializedUIField[]) => void,
    contextData: IContextData
    /**
     * key of the data
     * default is `form`
     * 
     * this will be used to get and set data 
     */
    dataKey?: string
    /**
     * call back when the data is changed
     */
    onChangeData?: (data: { [key: string]: any }) => void,
    /**
     * uxp context
     */
    uxpContext: IContextProvider,
    disabled?: boolean,
    isMobile?: boolean,
    droppableContainerId?: string,
    droppableParentContainerId?: string,
    proxyContainerId?: string // this will be used to filter the common fields using a secondary container id 
}

interface IFieldDropArgs {
    data: any;
    index: number;
    source: string;
    destination: string;
}


let droppableRegister: { [key: string]: (args: IFieldDropArgs) => void } = {};

export function useDrop(dropArgs: IUseDropArgs) {
    let { designer, uiProps, propPath, contextData, onChangeData, onChangeFields, uxpContext, dataKey, isMobile, droppableContainerId, droppableParentContainerId, disabled } = dropArgs

    const alerts = useAlert()

    if (!propPath) propPath = ['fields']
    if (!dataKey) dataKey = 'form'

    const droppableId = hasValue(droppableContainerId) ? droppableContainerId : `parent-${dataKey}`
    const disableDrop = dropArgs?.disabled || false

    let registeredFileds = getEditorFields();
    let fields: ISerializedUIField[] = extractPath(uiProps, propPath) || [];
    let fieldRef = React.useRef<ISerializedUIField[]>([])

    React.useEffect(() => {
        fieldRef.current = fields
    }, [fields])

    React.useEffect(() => {
        console.log('props.uiProps___props ', uiProps)
    }, [uiProps])

    React.useEffect(() => {
        droppableRegister[droppableId] = onDropField
        return () => {
            console.log('removing', droppableId);
            delete droppableRegister[droppableId];
        }
    }, [droppableId]);

    function filterItemsByContainerId(items: ISerializedUIField[], droppableContainerId: string) {
        // fallback for existing widgets 
        // if all fields doesn't have and containerId return all feilds  
        const _itemsWithContainerId = items.filter(i => hasValue(i?.droppableContainerId))
        if (_itemsWithContainerId.length == 0) return items

        if (!hasValue(droppableContainerId)) return items.filter(i => !hasValue(i.droppableContainerId))
        if (hasValue(dropArgs?.proxyContainerId)) return items.filter(i => (hasValue(i.droppableContainerId) && i.droppableContainerId == dropArgs.proxyContainerId))

        // filter by container 
        return items.filter(i => (hasValue(i.droppableContainerId) && i.droppableContainerId == droppableContainerId))
    }


    function pushField(field: ISerializedUIField, destination: string, index: number, droppableId: string, _fields: ISerializedUIField[]) {
        if (_fields.length == 0) {
            _fields.push(field)
            return _fields
        }
        // sort by droppable id 
        _fields.sort((a, b) => (a.droppableContainerId?.localeCompare(b?.droppableContainerId)))

        const itemsInTheContainer = _fields.filter(f => f.droppableContainerId === destination)
        const itemsNotInTheContainer = _fields.filter(f => f.droppableContainerId != destination)

        if (itemsInTheContainer.length > 0) itemsInTheContainer.splice(index, 0, field)
        else itemsInTheContainer.push(field)

        _fields = itemsNotInTheContainer.concat(itemsInTheContainer)

        return _fields
    }


    function updateAndGetFields(): ISerializedUIField[] {
        const __fields = fieldRef.current || []
        const _itemsWithContainerId = __fields.filter(i => hasValue(i?.droppableContainerId))
        if (_itemsWithContainerId.length == 0) {
            // if all fields doesnt have a container id 
            // set the current container id 
            for (const f of __fields) {
                f.droppableContainerId = droppableId
            }

            // finally update the ref in-case we use ref in any other place 
            fieldRef.current = __fields
        }
        return __fields
    }

    // on drop field 
    async function onDropField(args: IFieldDropArgs) {

        let { data, index, source, destination } = args

        let _fields = updateAndGetFields()

        // insert
        // widget fields 
        if (data?.type == 'fieldType') {

            let f = data.data as IUIFieldDefinition<IBaseProperty>;
            let name = `${dataKey}-field-${_fields.length + 1}-${generateUUID()}`
            let fieldId = name
            if (f.isInputField) {
                let inputs = _fields.filter(f => f.isInputField)
                name = `input${inputs.length + 1}`

                // show a popup and get the id 
                let formData = await alerts.form({
                    title: 'Give a name for the field',
                    formStructure: [
                        {
                            name: 'fieldId',
                            label: 'Name',
                            type: 'string',
                            validate: {
                                required: true
                            },
                            formatter(value: string) {
                                return cleanAttributeName(value)
                            },
                        }
                    ],
                    submitButtonTitle: 'Done',
                    hideCancelButton: false,
                    cancelButtonTitle: 'Cancel'
                })

                // stop processing 
                if (formData === null) return

                fieldId = formData.fieldId
                // update data 
                updateData(name, fieldId, null)
            }
            const fieldProps: any = { ...(f.defaultProps || {}), fieldLabel: { label: fieldId } }
            let item: ISerializedUIField = {
                name: name,
                type: f.id,
                fieldKey: `${f.id}###${f.description}`,
                metaType: f.type,
                props: fieldProps,
                isInputField: f.isInputField || false,
                id: fieldId,
                droppableContainerId: droppableId,
                dataKey: dataKey,
                propPath: propPath,
                disableDrop: disableDrop,
                onChangeFields: dropArgs.onChangeFields
            };

            // this will be used to generate default props 
            // ex - default columns in the horizontal list 
            if (!!f.onDropField) {
                item = f.onDropField(item)
            }

            const updated = pushField(item, destination, index, droppableId, _fields)
            onChangeFields(updated)
            return;
        }

        // mobile sections 
        // TODO :: update width above changes
        if (data?.type == 'mobileField') {

            let f = data.data as any;
            const fId = generateUUID()
            let item = {
                id: fId,
                name: fId,
                type: f.id,
                template: f,
                droppableContainerId: droppableId,
                dataKey: dataKey,
                propPath: propPath,
                disableDrop: disableDrop,
                onChangeFields: dropArgs.onChangeFields,
                props: { ...f.defaultProps || {}, fieldLabel: { label: fId } },  //Object.assign(f.defaultProps || {},{fieldLabel:{label:fieldId}}),
                isInputField: f.isInputField || false,
            };

            // this will be used to generate default props 
            // ex - default columns in the horizontal list 
            if (!!f.onDropField) {
                item = f.onDropField(item)
            }

            _fields.splice(index, 0, item);
            onChangeFields(_fields)
            return;
        }

        if (data?.type == 'widget') {

            let card_widget = data.data as ICardWidget;
            let name = `widget-field-${_fields.length + 1}`
            let fieldId = name

            let item: ISerializedUIField = {
                name: name,
                type: 'card-widget',
                metaType: 'card-widget',
                props: { widgetData: { ...card_widget || {} } },  //Object.assign(f.defaultProps || {},{fieldLabel:{label:fieldId}}),
                isInputField: false,
                id: fieldId,
            };

            // _fields.splice(index, 0, item);
            // onChangeFields(_fields)
            const updated = pushField(item, destination, index, droppableId, _fields)
            onChangeFields(updated)
            return;
        }

        // sort
        if (data?.type == 'field') {
            let { field, remover, source: sourceId, index: sourceIndex } = data.data;

            // remove field form source 
            const remaining = _fields.filter(f => f.id !== field.id)
            // push to destination 
            field.droppableContainerId = destination
            const updated = pushField(field, destination, index, droppableId, remaining)
            onChangeFields(updated)
            return;
        }

        alert('Unknown drop type:' + JSON.stringify(data));
    }

    function onRemoveField(id: string) {
        const updated = [...fieldRef.current].filter((f: any) => f.id !== id)
        onChangeFields(updated)
    }

    function updateData(name: string, id: string, value: any) {
        let _data = { ...contextData }
        if (!dataKey) dataKey = 'form'

        if (!_data) _data = {}
        if (!_data[dataKey]) _data[dataKey] = {}
        if (!_data[dataKey][id]) _data[dataKey][id] = null

        _data[dataKey][id] = value

        onChangeData?.(_data)
    }

    function createCardWidget(widget: ICardWidget, uxpContext: IContextProvider) {
        let cardWidget = findCardTemplate(widget.Template.__template__?.type)

        if (!cardWidget) return <></>

        return React.createElement(cardWidget?.template, {
            designer: null,
            uiProps: widget.Template?.uiProps,
            uxpContext: uxpContext,
            initialSource: null,
            isInnerField: true
        })
    }

    function renderRemoveButton(f: ISerializedUIField) {

        return <div className="wd-dnd-remove-field-btn"
            onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onRemoveField(f?.id)
            }
            }
        >
            <FontAwesomeIcon icon={['fas', 'trash']} />
        </div>
    }

    function generateFields(index?: number) {

        //  get fields belong to this container 
        const _allFields = [...fields]
        const _fields = filterItemsByContainerId([...fields], droppableId)
        return _fields.map((f, i) => {

            // pass the index to render the placeholder when draggina a field 
            // we have to do this because react-beautiful-dnd has some issue when using with react-grid-layout 
            // https://stackoverflow.com/questions/54982182/react-beautiful-dnd-drag-out-of-position-problem
            if (index != undefined && index != i) return null
            const globalIndex = _allFields.findIndex((af: ISerializedUIField) => af.id === f.id)

            let el: JSX.Element = null;

            if (f.type === 'card-widget') {
                let cardWidget = { ...f.props?.widgetData }

                // default props that need to pass to fields
                el = createCardWidget(cardWidget, dropArgs.uxpContext)

                return (
                    <DragWrapper data={{ field: f, remover: () => onRemoveField(f.id), source: f?.id }} type='field' index={i}>
                        {
                            (provided, snapshot) => {
                                return <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                    <div className="ui-field-wrapper">
                                        <SelectableItem
                                            designer={dropArgs.designer}
                                            props={[
                                                {
                                                    id: addToPath(propPath, globalIndex, 'id'),
                                                    value: f.id,
                                                    definition: { defaults: '', render: FieldCommonPropsEditor },

                                                },
                                                {
                                                    id: addToPath(propPath, globalIndex, 'props', 'visibility'),
                                                    value: f?.props?.visibility,
                                                    definition: VisibilityProperty(f.props.visibility)
                                                }
                                            ]}
                                            contextData={contextData}
                                        >
                                            <div>
                                                {el}
                                            </div>
                                            <IconButton size='small' className="wd-dnd-remove-field-btn"
                                                type='delete'
                                                onClick={() => onRemoveField(f?.id)}
                                            />
                                        </SelectableItem>
                                    </div>
                                </div>
                            }
                        }
                    </DragWrapper>
                );
            }

            let instanceId = f.id
            let field = getFieldByIdOrKey(f, registeredFileds);
            if (field) {
                let _props = { ...f.props }

                // default props that need to pass to fields
                _props.uxpContext = uxpContext
                _props.contextData = contextData

                // extra defaults for inputs (form components)
                if (f.isInputField) {
                    let value: any = null
                    if (contextData?.[dataKey]?.[f.id] != undefined) {
                        value = contextData[dataKey][f.id]
                    }
                    else if (f.props?.value != undefined) {
                        value = f.props.value
                    }
                    let onChange = (value: any) => {
                        updateData(f.name, f.id, value)
                    }

                    _props.value = value
                    _props.onChange = onChange
                }

                el = React.createElement(field.field, {
                    mode: dropArgs.designer?.mode,
                    fieldProps: Object.assign({}, _props),
                    uiProps,
                    uxpContext,
                    designer,
                    contextData: contextData,
                    onChangeContextData: onChangeData,
                    propPath,
                    dataKey,
                    droppableParentContainerId,
                    disableDrop,
                    onChangeFields: f.onChangeFields,
                    instanceId: instanceId,
                    index: globalIndex,
                });
            }
            else {

                // check if a mobile template 
                const mobileCards = getMobileCardTemplates()
                const card = mobileCards.find(mc => mc.id === f.type)

                if (card) {
                    el = React.createElement(card.template, {
                        designer: dropArgs.designer,
                        uiProps: f.props?.uiProps || {},
                        parentUIProps: uiProps,
                        uxpContext: dropArgs.uxpContext,
                        initialSource: null,
                        instanceId: instanceId,
                        index: globalIndex,
                        contextData: contextData,
                        onChangeContextData: onChangeData,
                        propPath,
                        dataKey,
                        droppableParentContainerId,
                        disableDrop,
                        onChangeFields: f.onChangeFields
                    })
                }
                else {
                    console.log('field------', f);

                    el = <div>Unknown field type: {f.type}</div>;
                }

            }

            // preview on select template or live 
            if (!designer || designer?.mode == 'thumbnail' || designer?.mode == 'live') {
                return <Conditional visible={evaluateConditions(f?.props?.visibility || { IsEither: true, Conditions: [], Groups: [] } as IQueryConditionGroup, contextData)} >
                    <div className="ui-field-wrapper" style={{ margin: `${f.props.margin?.top || 0}px ${f.props.margin?.right || 0}px ${f.props.margin?.bottom || 0}px ${f.props.margin?.left || 0}px` }}>
                        <SelectableItem designer={null} props={[]}>
                            {el}
                        </SelectableItem>
                    </div>
                </Conditional>
            }

            // render - design mode
            return <DragWrapper data={{ field: f, remover: () => onRemoveField(f.id), source: droppableId }} type='field' index={i}>
                {
                    (provided, snapshot) => {
                        return <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                            <div className="ui-field-wrapper">

                                {isMobile ? <div className="wd-selectable-item">
                                    {el}
                                    {/* <IconButton size='small' className="wd-dnd-remove-field-btn"
                                        type='delete'
                                        onClick={() => onRemoveField(f.id)}
                                    /> */}
                                    {renderRemoveButton(f)}
                                </div>
                                    :
                                    <SelectableItem
                                        designer={dropArgs.designer}
                                        props={[
                                            {
                                                id: addToPath(propPath, globalIndex, 'id'),
                                                value: f.id,
                                                definition: { defaults: '', render: FieldCommonPropsEditor },

                                            },
                                            {
                                                id: addToPath(propPath, globalIndex, 'props', 'visibility'),
                                                value: f?.props?.visibility || { IsEither: true, Conditions: [], Groups: [] } as IQueryConditionGroup,
                                                definition: VisibilityProperty(f?.props?.visibility || { IsEither: true, Conditions: [], Groups: [] })
                                            },
                                            {
                                                id: addToPath(propPath, globalIndex, 'props'),
                                                value: f.props,
                                                definition: { defaults: field?.defaultProps, render: field?.propertyEditor }
                                            },
                                            {
                                                id: addToPath(propPath, globalIndex, 'props', 'margin'),
                                                value: f?.props?.margin || 0,
                                                definition: MarginProperty(f?.props?.margin || { bottom: 0, top: 0, left: 0, right: 0, title: "Margin (in px)", hideRight: true, hideLeft: true })
                                            }
                                        ]}
                                        contextData={contextData}
                                        elementId={instanceId}
                                        style={{ margin: `${f.props.margin?.top || 0}px ${f.props.margin?.right || 0}px ${f.props.margin?.bottom || 0}px ${f.props.margin?.left || 0}px` }}
                                    >
                                        {el}
                                        {/* <IconButton size='small' className="wd-dnd-remove-field-btn"
                                            type='delete'
                                            onClick={() => onRemoveField(f.id)}
                                        /> */}
                                        {renderRemoveButton(f)}
                                    </SelectableItem>
                                }
                            </div>
                        </div>
                    }
                }
            </DragWrapper>;
        });

    }


    if (!dropArgs?.designer || dropArgs?.designer?.mode == 'thumbnail' || dropArgs?.designer?.mode == 'live') {
        return <div className='droppable-area-live'>
            {generateFields()}
        </div>;
    }

    let highlight = dropArgs?.designer?.selectedPanel == 'fields'

    return <Droppable
        droppableId={droppableId}
        // type={'parent'}
        isDropDisabled={disableDrop}
        // add clone api to fix the dragging element snapping out of the view 
        renderClone={(provided, snapshot, rubric) => (
            <div
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                ref={provided.innerRef}
            >
                {generateFields(rubric.source.index)}
            </div>
        )}
    >
        {
            (provided, snapshot) => {

                return <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className={classNames('droppable-area ', { 'dragging': snapshot.isDraggingOver, 'highlight': highlight })}
                    style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                >
                    {
                        generateFields()
                    }
                    {provided.placeholder}
                </div>;
            }
        }
    </Droppable>;
}

interface IDragWrapper {
    data: any;
    type: string;
    index: number;
    children: DraggableChildrenFn;
}

export const DragWrapper: React.FunctionComponent<IDragWrapper> = (props) => {
    let id = useTag({ index: props.index, type: props.type, data: props.data });
    return <Draggable draggableId={id} index={props.index} >
        {props.children}
    </Draggable>
}

export function triggerDrop(droppable: string, item: IFieldDropArgs) {
    droppableRegister[droppable]?.(item);
}

export async function getAllValuesForDropDown(parentProps: IWidgetTemplateProps, props: IDropdownFieldProperty, data: any) {
    console.log('calling to get all values ', props.binding)
    const _data = await executeAndBind(parentProps.uxpContext, props.binding, parentProps.uiProps?.datasources, data)
    if (!_data) return []
    return _data.map((d: any) => d[props.valueField || 'value'])
}
export async function extractDataFromFields(props: IWidgetTemplateProps, contextData: IContextData, fields: ISerializedUIField[], dataKey: string = 'form'): Promise<IContextData> {
    const _contextData = { ...contextData };

    if (!hasProperty(_contextData, `$.${dataKey}`)) {
        _contextData[dataKey] = {};
    }

    const inputs = fields.filter(f => f.isInputField);

    for (const field of inputs) {
        const fieldPath = formatFieldPath(`$.${dataKey}`, field.id);

        if (!hasProperty(_contextData, fieldPath) || containsExpressions(_contextData[dataKey][field.id])) {
            let val = _contextData[dataKey][field.id] ?? field.props?.value ?? null;
            if (typeof val == 'string' && containsExpressions(val)) {
                val = parseValue(val, _contextData)
            }

            // Set default values based on field type
            switch (field.type) {
                case 'date':
                case 'time':
                case 'datetime':
                    if (!hasValue(val)) val = new Date();
                    break;

                case 'daterange':
                    const now = new Date();
                    val = val || {};
                    if (!val.start) val.start = now;
                    if (!val.end) val.end = now;
                    // Store as ISO strings so expressions like {$.filters.myDate.start} render safely
                    val = {
                        start: val.start instanceof Date ? val.start.toISOString() : val.start,
                        end: val.end instanceof Date ? val.end.toISOString() : val.end,
                    };
                    break;

                case 'number':
                    if (!hasValue(val, true, field?.props?.allowNegativeValues)) {
                        val = field.props?.minValue ? Number(field.props.minValue) : 0;
                    }
                    break;

                case 'checkbox':
                    if (val === null) val = false;
                    break;

                case 'multiple-select':
                    if ((field.props as IDropdownFieldProperty).selectAllOnLoad) {
                        val = await getAllValuesForDropDown(props, field.props, contextData);
                    }
                    break;
            }

            _contextData[dataKey][field.id] = val;
        }
    }

    return _contextData;
}

const FieldCommonPropsEditor: React.FunctionComponent<IPropertyEditorProps<string>> = (props) => {

    return <PropertyContainer>
        <PropertyGroup>
            <PropertyGroupTitle>Field Id</PropertyGroupTitle>
            <Property>
                <Readonly
                    value={props.uiProps}
                />
            </Property>
        </PropertyGroup>
    </PropertyContainer>
}


export function generateSerializedField(id: string): ISerializedUIField {
    let fields = getUIFields();

    const field = fields.find(f => f.id == id)
    if (!field) return null

    const fieldId = generateUUID()
    const fieldProps: any = { ...(field.defaultProps || {}), fieldLabel: { label: fieldId } }
    let serialized: ISerializedUIField = {
        name: fieldId,
        type: field.id,
        metaType: field.type,
        props: fieldProps,  //Object.assign(f.defaultProps || {},{fieldLabel:{label:fieldId}}),
        isInputField: field.isInputField || false,
        id: fieldId,
        droppableContainerId: null,
        dataKey: null,
        propPath: null,
        disableDrop: false,
        onChangeFields: null
    };
    return serialized
}

export function renderField(field: ISerializedUIField, propPath: PropertyPath, globalIndex: number, designer: IWDDesignModeProps, uxpContext: IContextProvider, contextData: any, uiProps: any, onRemove: (f: ISerializedUIField) => void) {
    const dataKey = ''
    const registeredFileds = getEditorFields();
    const _field = getFieldByIdOrKey(field, registeredFileds)

    let _props = { ...field.props }
    // default props that need to pass to fields
    _props.uxpContext = uxpContext
    _props.data = contextData

    const el = React.createElement(_field.field, {
        mode: designer?.mode,
        fieldProps: Object.assign({}, _props),
        uiProps,
        uxpContext,
        designer,
        contextData: contextData,
        onChangeContextData: () => { },
        propPath,
        dataKey,
        droppableParentContainerId: null,
        disableDrop: false,
        onChangeFields: () => { },
    });

    if (!designer || designer?.mode == 'thumbnail' || designer?.mode == 'live') {
        return <Conditional visible={evaluateConditions(field?.props?.visibility || { IsEither: true, Conditions: [], Groups: [] } as IQueryConditionGroup, contextData)} >
            <SelectableItem designer={null} props={[]}>{el}</SelectableItem>
        </Conditional>
    }

    return <div className="uxp-ui-field-container">
        <SelectableItem
            designer={designer}
            props={[
                {
                    id: addToPath(propPath, globalIndex, 'props', 'visibility'),
                    value: field?.props?.visibility || { IsEither: true, Conditions: [], Groups: [] } as IQueryConditionGroup,
                    definition: VisibilityProperty(field?.props?.visibility || { IsEither: true, Conditions: [], Groups: [] })
                },
                {
                    id: addToPath(propPath, globalIndex, 'props'),
                    value: field.props,
                    definition: { defaults: _field?.defaultProps, render: _field?.propertyEditor }
                }
            ]}
            contextData={contextData}
        >
            {el}
            <div className="uxp-ui-field-remover" onClick={e => {
                e.preventDefault()
                e.stopPropagation()
                onRemove(field)
            }} >
                <FontAwesomeIcon icon={['fas', 'times']} />
            </div>
        </SelectableItem>
    </div>
}